import { type ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { presaleApi, type Presale } from '../../lib/presale-api';

function useCountdown(endsAt: string): string {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const tick = () => {
      const diff = new Date(endsAt).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft('Ended');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${h}h ${m}m ${s}s`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [endsAt]);

  return timeLeft;
}

function PresaleCard({ presale }: { presale: Presale }): ReactElement {
  const countdown = useCountdown(presale.endsAt);
  const progress = Math.min((Number(presale.totalRaised) / Number(presale.targetSol)) * 100, 100);
  const spotsLeft = presale.maxBuyers - presale.buyerCount;

  return (
    <Link to={`/presale/${presale.id}`} className="group block">
      <div className="notch-border-lg notch-border-white/8 relative overflow-hidden p-6 transition-all duration-300 [--notch-bg:rgba(255,255,255,0.02)] hover:[--notch-bg:rgba(255,255,255,0.04)] hover:[--notch-border-color:rgba(196,247,14,0.3)]">
        {/* Status badge */}
        <div className="absolute right-6 top-3">
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
              presale.status === 'OPEN'
                ? 'bg-[#c4f70e]/10 text-[#c4f70e]'
                : presale.status === 'LIVE'
                  ? 'bg-green-500/10 text-green-400'
                  : 'bg-white/10 text-white/60'
            }`}
          >
            {presale.status}
          </span>
        </div>

        {/* Token image + info */}
        <div className="mb-4 flex items-center gap-4">
          {presale.imageUrl ? (
            <img
              src={presale.imageUrl}
              alt={presale.tokenName}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#c4f70e]/10 text-lg font-bold text-[#c4f70e]">
              {presale.tokenSymbol.charAt(0)}
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-white transition-colors group-hover:text-[#c4f70e]">
              {presale.tokenName}
            </h3>
            <p className="font-mono text-sm text-white/40">${presale.tokenSymbol}</p>
          </div>
        </div>

        {/* Description */}
        {presale.description && (
          <p className="mb-4 line-clamp-2 text-sm text-white/50">{presale.description}</p>
        )}

        {/* Progress bar */}
        <div className="mb-3">
          <div className="mb-1 flex justify-between text-xs text-white/40">
            <span>{Number(presale.totalRaised).toFixed(2)} SOL raised</span>
            <span>{Number(presale.targetSol).toFixed(2)} SOL target</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white/[0.06]">
            <div
              className="h-full rounded-full bg-[#c4f70e] transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-between text-xs text-white/40">
          <span className="font-mono">{countdown}</span>
          <span>
            {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
          </span>
          <span>by {presale.launcher?.displayName || 'anon'}</span>
        </div>
      </div>
    </Link>
  );
}

export function PresaleDiscoverPage(): ReactElement {
  const [presales, setPresales] = useState<Presale[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    presaleApi
      .discover()
      .then(setPresales)
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Failed to load presales')
      )
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Presales</h1>
          <p className="mt-1 text-sm text-white/40">
            Curated token launches with creator fee redistribution
          </p>
        </div>
        <Link
          to="/presale/create"
          className="clip-notched inline-flex items-center gap-2 bg-[#c4f70e] px-4 py-2 text-sm font-medium text-black transition-colors hover:bg-[#c4f70e]/90"
        >
          Create Presale
        </Link>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-56 animate-pulse rounded-lg border border-white/[0.06] bg-white/[0.02]"
            />
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-4 text-sm text-red-400">
          {error}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && presales.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/[0.04]">
            <span className="text-2xl">🚀</span>
          </div>
          <h2 className="mb-2 text-lg font-semibold text-white">No presales yet</h2>
          <p className="mb-6 max-w-sm text-sm text-white/40">
            Be the first to create a curated token launch with creator fee redistribution.
          </p>
          <Link
            to="/presale/create"
            className="clip-notched inline-flex items-center gap-2 bg-[#c4f70e] px-4 py-2 text-sm font-medium text-black"
          >
            Create Presale
          </Link>
        </div>
      )}

      {/* Presale grid */}
      {!isLoading && presales.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {presales.map((presale) => (
            <PresaleCard key={presale.id} presale={presale} />
          ))}
        </div>
      )}
    </div>
  );
}
